<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { reveal } from '$lib/actions/reveal';
	import StatusDot from '$lib/components/ui/StatusDot.svelte';
	interface Event {
		ts: string;
		type: 'ok' | 'info' | 'warn';
		msg: string;
	}
	const events: Event[] = [
		{ ts: '14:22:03', type: 'info', msg: 'fleet.watch started · env=production · devices=8,412' },
		{ ts: '14:22:04', type: 'ok', msg: 'edge-prod-aus-0187 → heartbeat OK · cpu 12% · mem 41%' },
		{ ts: '14:22:04', type: 'ok', msg: 'edge-prod-usa-2243 → heartbeat OK · cpu 8% · mem 37%' },
		{ ts: '14:22:05', type: 'info', msg: 'rollout/signage-v2.14.0 → canary 5% · 420/8412 devices' },
		{ ts: '14:22:06', type: 'ok', msg: 'edge-prod-eu-0891 → rootfs v2.14.0 verified · sig ok' },
		{ ts: '14:22:06', type: 'warn', msg: 'edge-prod-usa-0031 → latency 124ms · soak threshold ok' },
		{ ts: '14:22:07', type: 'ok', msg: 'rollout/signage-v2.14.0 → health check passed · 3/3' },
		{
			ts: '14:22:08',
			type: 'info',
			msg: 'rollout/signage-v2.14.0 → expanding to 25% · 2,103 devices'
		},
		{
			ts: '14:22:09',
			type: 'ok',
			msg: 'edge-prod-jpn-0442 → live telemetry streaming · 8.2k events'
		},
		{
			ts: '14:22:10',
			type: 'ok',
			msg: 'rollout/signage-v2.14.0 → complete · 8,412/8,412 devices · 89s'
		}
	];
	let visibleLines = $state<(Event & { id: number })[]>([]);
	let idx = 0;
	let counter = 0;
	let interval: ReturnType<typeof setInterval> | null = null;
	let container: HTMLDivElement;
	function addLine() {
		const e = events[idx % events.length];
		visibleLines = [...visibleLines, { ...e, id: counter++ }];
		if (visibleLines.length > 10) visibleLines = visibleLines.slice(-10);
		idx++;
	}
	onMount(() => {
		for (let i = 0; i < 6; i++) addLine();
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !interval) {
						interval = setInterval(addLine, 1400);
					} else if (!entry.isIntersecting && interval) {
						clearInterval(interval);
						interval = null;
					}
				});
			},
			{ threshold: 0.3 }
		);
		if (container) observer.observe(container);
		return () => observer.disconnect();
	});
	onDestroy(() => {
		if (interval) clearInterval(interval);
	});
	function typeClass(t: Event['type']) {
		return t === 'ok' ? 'term-ok' : t === 'warn' ? 'term-warn' : 'term-info';
	}
	function typeLabel(t: Event['type']) {
		return t === 'ok' ? '✓' : t === 'warn' ? '!' : '→';
	}
</script>

<section class="mx-auto mt-20 max-w-[1216px] px-4 sm:mt-32 lg:mt-40 lg:px-0">
	<div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
		<div class="reveal" use:reveal>
			<span
				class="text-blue-soft mb-4 inline-block font-display text-[11px] font-medium uppercase tracking-[0.2em]"
			>
				Real-time fleet control
			</span>
			<h2
				class="text-gradient-enterprise mb-5 font-display text-3xl font-medium leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl"
			>
				See your fleet. Command your fleet.
			</h2>
			<p class="mb-8 font-display text-base leading-relaxed text-white/65 sm:text-lg">
				Every state change, every rollout, every heartbeat — streamed to a single pane of glass.
				Debug production incidents in seconds, not days.
			</p>
			<ul class="space-y-4">
				{#each ['Live telemetry streams with sub-second latency', 'Remote screenshots and device diagnostics without ever exposing an inbound port', 'Comprehensive API and webhooks for every fleet action'] as item}
					<li class="flex items-start gap-3">
						<div class="bg-blue mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full"></div>
						<span class="font-display text-base leading-relaxed text-white/75">{item}</span>
					</li>
				{/each}
			</ul>
		</div>
		<div class="reveal reveal-delay-2 min-w-0" use:reveal>
			<div class="terminal terminal-mobile w-full overflow-hidden rounded-xl shadow-2xl">
				<div
					class="flex items-center gap-2 border-b border-white/10 bg-white/[0.02] px-3 py-3 sm:px-4"
				>
					<div class="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FF5F57]"></div>
					<div class="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#FFBD2E]"></div>
					<div class="h-2.5 w-2.5 flex-shrink-0 rounded-full bg-[#28CA41]"></div>
					<span class="ml-2 truncate font-mono text-[10px] text-white/40 sm:ml-3 sm:text-xs">
						admiral fleet watch
					</span>
					<span
						class="ml-auto flex flex-shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[#10B981]"
					>
						<StatusDot />
						Live
					</span>
				</div>
				<div bind:this={container} class="min-h-[300px] p-3 sm:min-h-[340px] sm:p-5">
					{#each visibleLines as line (line.id)}
						<div class="terminal-line break-words">
							<span class="term-ts">{line.ts}</span>
							<span class={typeClass(line.type)}>{typeLabel(line.type)}</span>
							<span class="term-white">{line.msg}</span>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</div>
</section>

<style>
	.terminal-mobile {
		font-size: 11px;
		line-height: 1.7;
	}
	@media (min-width: 640px) {
		.terminal-mobile {
			font-size: 13px;
			line-height: 1.85;
		}
	}
	.terminal-line {
		overflow-wrap: anywhere;
		word-break: break-word;
	}
</style>
